import { BrowserRouter, Switch } from 'react-router-dom';
import { ApmRoute } from '@elastic/apm-rum-react';
import { Authentication, PrivateRoute, Login, RenewLogin } from '@zen/identity-lib';
import Layout from './components/Layout';
import { Api } from './contexts/api';
import ConfigurationContext, {
  ConfigurationProvider,
  ConfigurationProps,
} from './contexts/configuration';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';
import Invoice from './pages/Invoice';
import PaymentSuccess from './pages/PaymentSuccess';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import TelemetryProvider from './TelemetryProvider';
import { getAppInsights } from './TelemetryService';
import RegexRedirect from './components/RegexRedirect';
import AuthSpinner from './components/AuthSpinner';
import setupApm from './utils/apm';
import FindInvoice from './pages/FindInvoice';

function App() {
  const routes = (config: ConfigurationProps) => {
    const {
      publicGatewayApiUrl,
      bssGatewayApiUrl,
      identityConfig,
      authorisationApiBaseUrl,
      iKey,
      apmBaseUrl,
      environment,
      version,
    } = config;

    setupApm(apmBaseUrl, version, environment);

    return (
      <div className="App">
        <Layout>
          <BrowserRouter>
            <TelemetryProvider instrumentationKey={iKey} after={getAppInsights}>
              <ErrorBoundary>
                <Authentication
                  config={identityConfig}
                  authorisationApiBaseUrl={authorisationApiBaseUrl}
                  unauthorisedComponent={() => <div>Unauthorised</div>}
                  authorisationResultOverride={true}
                  loadingComponent={LoadingSpinner}
                >
                  <AuthSpinner />
                  <Api apiBaseUrl={publicGatewayApiUrl} bssApiBaseUrl={bssGatewayApiUrl}>
                    <Switch>
                      <PrivateRoute path="/" exact component={NotFound} />
                      <ApmRoute path="/login" exact component={Login} />
                      <ApmRoute path="/renewlogin" exact component={RenewLogin} />
                      <PrivateRoute path="/invoice/:id/:authNumber" exact component={Invoice} />
                      <PrivateRoute path="/find-invoice" exact component={FindInvoice} />
                      <PrivateRoute
                        path="/success/:id/:authNumber/:cardTransactionId"
                        exact
                        component={PaymentSuccess}
                      />

                      <RegexRedirect
                          path="/ViewInvoice.aspx"
                          from="/ViewInvoice.aspx\?i=(\w+)&a=(\w+)"
                          to="/invoice/$1/$2"
                      />
                      <RegexRedirect
                        path="/ViewInvoice.htm"
                        from="/ViewInvoice.htm\?i=(\w+)&a=(\w+)"
                        to="/invoice/$1/$2"
                      />
                      <RegexRedirect
                        path="/FindInvoice.aspx"
                        from="/FindInvoice.aspx"
                        to="/find-invoice"
                      />
                      <ApmRoute component={NotFound} />
                    </Switch>
                  </Api>
                </Authentication>
              </ErrorBoundary>
            </TelemetryProvider>
          </BrowserRouter>
        </Layout>
      </div>
    );
  };

  return (
    <ConfigurationProvider
      configPath={`/config/config.json?v=${process.env.REACT_APP_VERSION}`}
      loadingComponent={() => (
        <Layout>
          <LoadingSpinner />
        </Layout>
      )}
    >
      <ConfigurationContext.Consumer>
        {(config: ConfigurationProps | null) => {
          if (!config) {
            return <div>Config failed to load</div>;
          }
          return routes(config!);
        }}
      </ConfigurationContext.Consumer>
    </ConfigurationProvider>
  );
}

export default App;
